import { useSectionAccess } from "../../auth/useSectionAccess";
import { useEffect, useMemo, useState } from "react";
import type { FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { marketsApi, type MarketView } from "../../api/markets";
import { COUNTRIES, flagEmoji } from "../../i18n/countries";
import { useToast } from "../../components/Toast";
import { useConfirm } from "../../components/ConfirmDialog";
import type { ApiError } from "../../api/client";
import { localizedName } from "../../lib/localizedName";

function countryName(code: string | null | undefined): string {
  if (!code) return "";
  return COUNTRIES.find((c) => c.code === code)?.name ?? code;
}

export default function AdminMarketsPage() {
  const { canWrite, canFull } = useSectionAccess("market_settings");
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const confirm = useConfirm();

  const [markets, setMarkets] = useState<MarketView[]>([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState<MarketView | "new" | null>(null);

  async function refresh() {
    setLoading(true);
    try { setMarkets(await marketsApi.list()); } finally { setLoading(false); }
  }

  useEffect(() => { void refresh(); }, []);

  // Map of country -> market that owns it (for grayed-out hint in editor)
  const ownerByCountry = useMemo(() => {
    const m = new Map<string, MarketView>();
    for (const mk of markets) {
      for (const c of mk.countryCodes) m.set(c, mk);
    }
    return m;
  }, [markets]);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div className="page-head" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <h1 style={{ margin: 0, fontSize: 22, color: "var(--ink)" }}>
          {t("admin.markets.title", "Markets")}
        </h1>
        <button type="button" className="btn primary" disabled={!canWrite} onClick={() => setEditing("new")}>
          + {t("admin.markets.new", "New market")}
        </button>
      </div>

      {loading ? (
        <div style={{ color: "var(--ink-3)" }}>{t("common.loading", "Loading…")}</div>
      ) : markets.length === 0 ? (
        <div style={{ color: "var(--ink-3)" }}>{t("admin.markets.empty", "No markets yet.")}</div>
      ) : (
        <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: 6 }}>
          {markets.map(m => (
            <li key={m.id} className="card" style={{ padding: 12 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600 }}>{localizedName(m, i18n.resolvedLanguage ?? "en")}</div>
                  <div style={{ fontSize: 12, color: "var(--ink-3)", marginTop: 2 }}>
                    {m.countryCodes.length === 0
                      ? t("admin.markets.noCountries", "No countries")
                      : m.countryCodes.map((c) => `${flagEmoji(c)} ${countryName(c)}`).join(", ")}
                  </div>
                  {m.description && (
                    <div style={{ fontSize: 12, color: "var(--ink-3)", marginTop: 2 }}>{m.description}</div>
                  )}
                </div>
                <button type="button" className="btn sm" disabled={!canWrite} onClick={() => setEditing(m)}>
                  {t("action.edit", "Edit")}
                </button>
                <button type="button" className="btn sm" disabled={!canFull} onClick={async () => {
                  if (!(await confirm({ message: t("admin.markets.confirmDelete", "Delete this market?"), danger: true }))) return;
                  try {
                    await marketsApi.delete(m.id);
                    await refresh();
                    toast.success(t("admin.markets.deleted", "Market deleted."));
                  } catch (err) { toast.error((err as ApiError).message); }
                }}>
                  {t("action.delete", "Delete")}
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {editing && canWrite && (
        <MarketEditor
          market={editing === "new" ? null : editing}
          ownerByCountry={ownerByCountry}
          onClose={() => setEditing(null)}
          onSaved={async () => { await refresh(); setEditing(null); }}
        />
      )}
    </div>
  );
}

function MarketEditor({
  market,
  ownerByCountry,
  onClose,
  onSaved,
}: {
  market: MarketView | null;
  ownerByCountry: Map<string, MarketView>;
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const [name, setName] = useState(market?.name ?? "");
  const [description, setDescription] = useState(market?.description ?? "");
  const [selected, setSelected] = useState<Set<string>>(new Set(market?.countryCodes ?? []));
  const [busy, setBusy] = useState(false);

  function toggle(code: string) {
    const next = new Set(selected);
    if (next.has(code)) next.delete(code);
    else next.add(code);
    setSelected(next);
  }

  async function save(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      const body = {
        name,
        description: description || null,
        countryCodes: Array.from(selected),
      };
      if (market) await marketsApi.update(market.id, body);
      else await marketsApi.create(body);
      await onSaved();
      toast.success(t("admin.markets.saved", "Market saved."));
    } catch (err) {
      toast.error((err as ApiError).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={{
      position: "fixed", inset: 0, background: "var(--overlay)",
      display: "grid", placeItems: "center", zIndex: 100, padding: 24,
    }}>
      <form onSubmit={save} className="card" style={{ width: "min(680px, 100%)", maxHeight: "90vh", display: "flex", flexDirection: "column" }}>
        <div className="card-b" style={{ display: "flex", flexDirection: "column", gap: 14, overflowY: "auto" }}>
          <h2 style={{ margin: 0, fontSize: 18 }}>
            {market ? t("admin.markets.edit", "Edit market") : t("admin.markets.new", "New market")}
          </h2>
          <label className="field">
            <span>{t("admin.markets.nameLabel", "Name")} *</span>
            <input className="input" value={name} onChange={(e) => setName(e.target.value)} required maxLength={120} />
          </label>
          <label className="field">
            <span>{t("admin.markets.description", "Description")}</span>
            <textarea className="input" rows={2} value={description} onChange={(e) => setDescription(e.target.value)} />
          </label>
          <div className="field">
            <span style={{ fontSize: 11.5, fontWeight: 600, color: "var(--ink-3)", letterSpacing: ".02em", textTransform: "uppercase" }}>
              {t("admin.markets.countries", "Countries")}
            </span>
            <div className="dd-scroll" style={{
              display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))",
              gap: 4, padding: 10, border: "1px solid var(--border)", borderRadius: 6,
              maxHeight: 320, overflowY: "auto", background: "var(--surface)",
            }}>
              {COUNTRIES.map((c) => {
                const owner = ownerByCountry.get(c.code);
                const ownedByOther = owner && (!market || owner.id !== market.id);
                const checked = selected.has(c.code);
                return (
                  <label key={c.code} style={{
                    display: "flex", alignItems: "center", gap: 6,
                    padding: "4px 6px", borderRadius: 4,
                    fontSize: 13, cursor: ownedByOther ? "not-allowed" : "pointer",
                    opacity: ownedByOther ? 0.45 : 1,
                  }}>
                    <input
                      type="checkbox"
                      checked={checked}
                      disabled={ownedByOther || busy}
                      onChange={() => toggle(c.code)}
                    />
                    <span aria-hidden style={{ fontSize: 16, lineHeight: 1 }}>{flagEmoji(c.code)}</span>
                    <span style={{ flex: 1, color: "var(--ink-2)" }}>{c.name}</span>
                    {ownedByOther && (
                      <span style={{ fontSize: 11, color: "var(--ink-3)" }}>· {owner!.name}</span>
                    )}
                  </label>
                );
              })}
            </div>
          </div>
        </div>
        <div style={{
          display: "flex", gap: 8, justifyContent: "flex-end",
          padding: "14px 18px", borderTop: "1px solid var(--border)",
          background: "var(--surface-2)", borderRadius: "0 0 var(--r-md) var(--r-md)",
        }}>
          <button type="button" className="btn" onClick={onClose}>
            {t("action.cancel", "Cancel")}
          </button>
          <button type="submit" className="btn primary" disabled={busy}>
            {busy ? t("action.saving", "Saving…") : t("action.save", "Save")}
          </button>
        </div>
      </form>
    </div>
  );
}
